import React, { useState, useRef, useEffect } from "react";

import {
    Box,
    Button,
    Container,
    Typography,
    ThemeProvider,
    ScopedCssBaseline,
    Grid,
    IconButton,
    Tooltip,
    Badge,
    useMediaQuery,
} from "@mui/material";
import { Add, Tune, PictureAsPdf } from "@mui/icons-material";
import theme from "../../../material-ui-common/theme";

import { useQuery } from "react-query";
import { getTechniciansNonPaged, getCalendarEvents } from "./query-functions";

import TechniciansCalendar from "./calendar";
import CreateTechnicianModal from "./create-modal";
import EventDetails from "./event-details";
import FilterModal from "./filter-modal";
import ManageTechniciansView from "./manage-technicians";

import RestrictedComponent from "../../../lib/components/form/RestrictedComponent";
import schema from "../../../routes/permissions/schema.js";

import moment from "moment";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { groupBy } from "underscore";

const formatString = "YYYY-MM-DD HH:mm:ss";

const defaultFilterValue = {
    technicians: [],
};

const TechnicianListing = (props) => {
    const clientID = props.credentials.clientID;
    const userInfo = props.credentials.user;

    const isMobileScreenSize = useMediaQuery("(max-width:925px)");
    // export the calendar to a PDF with the tech name and the date range
    // refactor the dot on the calendar events

    const [filter, setFilter] = useState(defaultFilterValue);
    const [filterModalOpen, setFilterModalOpen] = useState(false);

    const [selectedEvent, setSelectedEvent] = useState(null);
    const [manageButtonAnchorEl, setManageButtonAnchorEl] = useState(null);
    const [createTechnicianModalOpen, setCreateTechnicianModalOpen] =
        useState(false);

    const [dateFilter, setDateFilter] = useState({
        startDate: moment().startOf("day").format(formatString), // the start depends on the default view of the calendar (month, week, day, agenda)
        endDate: moment().endOf("day").add(1, "month").format(formatString),
    });

    const { data: technicians } = useQuery(
        "technicians",
        () => getTechniciansNonPaged(clientID),
        {
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
            retry: false,
        }
    );

    const {
        data: calendarEvents,
        refetch: refetchCalendarEvents,
        isLoading: calendarEventsIsLoading,
        isFetching: isCalendarEventsFetching,
    } = useQuery(`technicianCalendarEvents`, () =>
        getCalendarEvents({
            clientID,
            startDateTime: dateFilter.startDate,
            stopDateTime: dateFilter.endDate,
        })
    );

    const [filteredEvents, setFilteredEvents] = useState(null);
    const [isFilterEventsLoading, setIsFilterEventsLoading] = useState(false);

    const firstUpdate = useRef(true);
    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        if (filter.technicians.length > 0) {
            filterCalendarEvents(filter.technicians);
            return;
        }

        refetchCalendarEvents();
    }, [dateFilter]);

    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        if (!technicians) return;

        const associatedUser = technicians.find(
            (tech) => tech.userId === userInfo.userId
        );

        if (associatedUser && filter.technicians.length === 0) {
            filterCalendarEvents([associatedUser]);
        }
    }, [technicians]);

    const filterCalendarEvents = (techniciansToFilterFor) => {
        setFilter((prevFilter) => ({
            ...prevFilter,
            technicians: techniciansToFilterFor,
        }));

        if (techniciansToFilterFor.length === 0) {
            setFilteredEvents(null); // will make the calendar show calendarEvents instead of filteredEvents

            if (!calendarEvents) {
                refetchCalendarEvents();
            }

            return;
        }

        const promises = techniciansToFilterFor.map((tech) => {
            return getCalendarEvents({
                clientID,
                startDateTime: dateFilter.startDate,
                stopDateTime: dateFilter.endDate,
                techID: tech.techID,
            });
        });

        setIsFilterEventsLoading(true);

        Promise.all(promises)
            .then((results) => {
                setFilteredEvents(results.flat());
                setIsFilterEventsLoading(false);
            })
            .catch((err) => {
                setIsFilterEventsLoading(false);
            });
    };

    return (
        <ThemeProvider theme={theme}>
            <Container
                maxWidth={false}
                sx={{ padding: "5px 15px !important", height: "100%" }}
            >
                <Box
                    component={"header"}
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                    }}
                >
                    <Typography variant="h3" component="h1">
                        Technicians
                    </Typography>

                    <div>
                        <RestrictedComponent
                            permissionCheck={
                                schema.MANAGE_CLIENT_TECHNICIANS.CREATE
                            }
                        >
                            <Tooltip title="Create New Technician">
                                <IconButton
                                    onClick={(e) =>
                                        setCreateTechnicianModalOpen(true)
                                    }
                                    sx={{ mr: 1 }}
                                >
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        </RestrictedComponent>

                        <Tooltip title="Filter Events By">
                            <IconButton
                                onClick={() =>
                                    setFilterModalOpen(!filterModalOpen)
                                }
                                sx={{ mr: 1 }}
                            >
                                <Badge badgeContent={filter.technicians.length}>
                                    <Tune />
                                </Badge>
                            </IconButton>
                        </Tooltip>

                        <RestrictedComponent
                            permissionCheck={
                                schema.MANAGE_CLIENT_TECHNICIANS.UPDATE
                            }
                        >
                            <Button
                                variant="contained"
                                onClick={(e) =>
                                    setManageButtonAnchorEl(e.target)
                                }
                            >
                                Manage Technicians
                            </Button>
                        </RestrictedComponent>
                    </div>
                </Box>

                <ScopedCssBaseline
                    sx={{ borderRadius: "10px", height: "100%" }}
                >
                    {filterModalOpen && (
                        <FilterModal
                            open={filterModalOpen}
                            setOpen={setFilterModalOpen}
                            technicians={technicians}
                            filter={filter}
                            setFilter={setFilter}
                            setFilteredEvents={setFilteredEvents}
                            filterCalendarEvents={filterCalendarEvents}
                            defaultFilterValue={defaultFilterValue}
                        />
                    )}

                    {selectedEvent && (
                        <EventDetails
                            clientID={clientID}
                            selectedEvent={selectedEvent}
                            setSelectedEvent={setSelectedEvent}
                            technicians={technicians}
                            emitSnack={props.emitSnack}
                        />
                    )}

                    {createTechnicianModalOpen && (
                        <CreateTechnicianModal
                            clientID={clientID}
                            technicians={technicians}
                            open={createTechnicianModalOpen}
                            setOpen={setCreateTechnicianModalOpen}
                            emitSnack={props.emitSnack}
                        />
                    )}

                    {Boolean(manageButtonAnchorEl) && (
                        <ManageTechniciansView
                            clientID={clientID}
                            technicians={technicians}
                            open={Boolean(manageButtonAnchorEl)}
                            manageButtonAnchorEl={manageButtonAnchorEl}
                            setManageButtonAnchorEl={setManageButtonAnchorEl}
                            emitSnack={props.emitSnack}
                        />
                    )}
                </ScopedCssBaseline>
                <Grid
                    container
                    spacing={0}
                    sx={{
                        mt: "10px",
                        backgroundColor: "white",
                        p: isMobileScreenSize ? 1 : 2,
                        pb: isMobileScreenSize ? 1 : 1.5,
                        borderRadius: "10px",
                    }}
                >
                    <Grid item xs={12}>
                        <TechniciansCalendar
                            clientID={clientID}
                            calendarEvents={filteredEvents || calendarEvents}
                            setDateFilter={setDateFilter}
                            dateFilter={dateFilter}
                            formatString={formatString}
                            setSelectedEvent={setSelectedEvent}
                            isLoading={
                                isFilterEventsLoading ||
                                calendarEventsIsLoading ||
                                isCalendarEventsFetching
                            }
                            isMobileScreenSize={isMobileScreenSize}
                        />
                    </Grid>

                    {/* export schedule to pdf */}
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                mt: 2,
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={() =>
                                    handleExportScheduleToPdf({
                                        filter,
                                        dateFilter,
                                        calendarEvents:
                                            filteredEvents || calendarEvents,
                                    })
                                }
                                startIcon={<PictureAsPdf />}
                            >
                                Export Schedule
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </ThemeProvider>
    );
};

export default TechnicianListing;

const handleExportScheduleToPdf = ({ filter, dateFilter, calendarEvents }) => {
    const techNamesWithSpaces = filter.technicians
        .map((tech) => tech.techName)
        .join(", ");

    const techNamesWithAnd = techNamesWithSpaces.replace(
        /,(?!.*,)/gim,
        ", and"
    );

    const pdf = new jsPDF("p", "pt", "letter");
    pdf.setFontSize(18);

    let pageTitle = "Schedule";
    if (techNamesWithSpaces) {
        pageTitle += ` for ${techNamesWithAnd}`;
    } else {
        pageTitle = "Event Schedule";
    }
    pdf.text(40, 40, pageTitle);

    pdf.setFontSize(12);
    pdf.text(
        40,
        60,
        `${moment(dateFilter.startDate).format("MMMM Do YYYY")} - ${moment(
            dateFilter.endDate
        ).format("MMMM Do YYYY")}`
    );

    if (calendarEvents.length === 0) {
        pdf.text(40, 80, "There are no events to display");
        pdf.save("event_schedule.pdf");
        return;
    }

    const groupedEvents = groupBy(calendarEvents, (event) =>
        moment(event.start).format("YYYY-MM-DD")
    );

    // sort the events by date
    const sortedEvents = Object.keys(groupedEvents)
        .sort((a, b) => {
            return moment(a).diff(moment(b));
        })
        .reduce((obj, key) => {
            obj[key] = groupedEvents[key];
            return obj;
        }, {});

    // take the sorted events and create a table
    const table = Object.keys(sortedEvents).map((date) => {
        const events = sortedEvents[date];

        const rows = events.map((event, i) => {
            const date =
                i === 0 ? moment(event.start).format("ddd MMM DD") : "";
            const time = `${moment(event.start).format("h:mm a")} - ${moment(
                event.end
            ).format("h:mm a")}`;
            const status = event.status;
            const title = event.title;

            const techNames = event.technicians.join(", ");

            return [date, time, status, title, techNames];
        });

        return rows;
    });

    const tableHeaders = ["Date", "Time", "Status", "Event Name", "Techs"];
    const tableRows = table.flat();

    pdf.autoTable({
        head: [tableHeaders],
        body: tableRows,
        startY: 80,
        theme: "striped",
        styles: {
            overflow: "linebreak",
            fontSize: 10,
        },
        headStyles: {
            fillColor: "#f1f1f1",
            fillColor: "#457093",
        },
    });

    let fileName;

    if (filter.technicians.length > 0) {
        const techNamesCommaSeparated = filter.technicians
            .map((tech) => tech.techName)
            .join(",");

        fileName =
            techNamesCommaSeparated.replaceAll(" ", "-") + "_schedule.pdf";
    } else {
        fileName = "event_schedule";
    }

    pdf.save(fileName);
};
